import {
  ADMIN_ROLE,
  ADVERTISER_ROLE,
  DESIGNER_ROLE,
  MARKETER_ROLE,
  OWNER_ROLE,
  SUPER_ADMIN_ROLE,
  VIEWER_ROLE,
} from "../../../utils/constants";

/**
 * Platform/shop role order: most important first (aligned with ROLES_PLATFORMS + remaining ROLES_SHOPS).
 * Unknown roles sort after these, then by name/email.
 */
const ROLE_ORDER_LIST = [
  ADVERTISER_ROLE,
  MARKETER_ROLE,
  VIEWER_ROLE,
  OWNER_ROLE,
  DESIGNER_ROLE,
  SUPER_ADMIN_ROLE,
  ADMIN_ROLE,
];

const getRolePriority = (role) => {
  if (!role || typeof role !== "string") return 1000;
  const idx = ROLE_ORDER_LIST.indexOf(role);
  return idx >= 0 ? idx : 1000;
};

/**
 * @param {Array<{ role?: string, name?: string, email?: string }>} users
 * @returns {Array<typeof users[number]>}
 */
export const sortUsersByRolePriority = (users) => {
  if (!Array.isArray(users)) return [];
  return [...users].sort((a, b) => {
    const pa = getRolePriority(a?.role);
    const pb = getRolePriority(b?.role);
    if (pa !== pb) return pa - pb;
    const na = `${a?.name || ""}${a?.email || ""}`;
    const nb = `${b?.name || ""}${b?.email || ""}`;
    return na.localeCompare(nb);
  });
};

/**
 * Merge users from multiple advertisers under an agency; dedupe by email (case-insensitive),
 * keeping the user with the highest role priority when duplicates exist.
 * @param {{ advertisers?: Array<{ advertiser?: { users?: unknown[] } }> }} agencyGroup
 */
export const mergeUsersForAgencyEmail = (agencyGroup) => {
  const advertisers = agencyGroup?.advertisers;
  if (!Array.isArray(advertisers)) return [];

  const byEmail = new Map();

  for (const item of advertisers) {
    const users = item?.advertiser?.users;
    if (!Array.isArray(users)) continue;
    for (const u of users) {
      if (!u?.email || typeof u.email !== "string") continue;
      const key = u.email.trim().toLowerCase();
      if (!key) continue;
      const existing = byEmail.get(key);
      if (
        !existing ||
        getRolePriority(u.role) < getRolePriority(existing.role)
      ) {
        byEmail.set(key, u);
      }
    }
  }

  return sortUsersByRolePriority([...byEmail.values()]);
};
